/**
 * 由 businessReportList 日度 list 加工出运营分析各组件数据（房态健康、客源、折扣、渠道分析/信息、出租率、运营效率）
 * 与后端 storeRevenueService 的 process* 逻辑对齐，供前端直调 PMS 后本地使用
 */

import {
  formatDateInChina,
  formatTimestampToChinaDate,
  getYesterdayStrInChina,
  parseChinaDate,
} from '../../../utils/dateChina';

export type GranularityType = '日度' | '日度7天' | '日度30天' | '月度';

function getDataCountByGranularity(granularity: GranularityType): number {
  if (granularity === '月度') return 12;
  if (granularity === '日度7天') return 7;
  if (granularity === '日度30天') return 30;
  return 30;
}

function isDailyGranularity(granularity: GranularityType): boolean {
  return granularity === '日度' || granularity === '日度7天' || granularity === '日度30天';
}

/** 日度：以中国「上一营业日」为结束日取过去 N 天（接口不支持当天）；月度：取最近 N 个月 */
function getRecentDataByGranularity(dataList: any[], granularity: GranularityType): any[] {
  const dataCount = getDataCountByGranularity(granularity);
  if (granularity === '月度') {
    return dataList.slice(-dataCount);
  }
  const endStr = getYesterdayStrInChina();
  const dateStrs: string[] = [];
  for (let i = dataCount - 1; i >= 0; i--) {
    const d = new Date(parseChinaDate(endStr).getTime() - i * 86400 * 1000);
    dateStrs.push(formatDateInChina(d));
  }
  const byDate = new Map<string, any>();
  for (const item of dataList) {
    const key = formatTimestampToChinaDate(item.date);
    byDate.set(key, item);
  }
  const placeholder = (dateStr: string) => {
    const ts = Math.floor(parseChinaDate(dateStr).getTime() / 1000);
    return {
      date: ts,
      discountRate: 0,
      occupancyRate: 0,
      avgListPrice: 0,
      avgPrice: 0,
      roomAvaliable: 0,
      roomRepair: 0,
      roomLock: 0,
      roomTotal: 0,
      otaRoomNight: 0,
      unitRoomNight: 0,
      introRoomNight: 0,
      memberRoomNight: 0,
      fitRoomNight: 0,
      teamRoomNight: 0,
      roomOvernightRate: 0,
      hourScale: 0,
      roomNight: 0,
      earlyCheckout: 0,
      noshow: 0,
      roomOvernight: 0,
      orderRoomTake: 0,
      adr: 0,
    };
  };
  return dateStrs.map((dateStr) => byDate.get(dateStr) ?? placeholder(dateStr));
}

const CUSTOMER_SOURCE_SERIES_COLORS: Record<string, string> = {
  ota: '#5470c6',
  unit: '#91cc75',
  intro: '#fac858',
  member: '#ee6666',
  fit: '#73c0de',
  team: '#3ba272',
  occupancyRate: '#1890ff',
};

function processRoomHealthData(dataList: any[], granularity: GranularityType): any {
  const recentData = getRecentDataByGranularity(dataList, granularity);
  const categories = recentData.map((item) => {
    const date = new Date(item.date * 1000);
    return isDailyGranularity(granularity) ? `${date.getMonth() + 1}/${date.getDate()}` : `${date.getMonth() + 1}月`;
  });
  const available = recentData.map((item) => item.roomAvaliable || 0);
  const repair = recentData.map((item) => item.roomRepair || 0);
  const locked = recentData.map((item) => item.roomLock || 0);
  const total = recentData.map((item) => item.roomTotal || 0);
  const ratio = repair.map((r, i) => {
    const totalCount = total[i] || 1;
    const abnormalCount = r + locked[i];
    return totalCount > 0 ? Math.round((abnormalCount / totalCount) * 100 * 100) / 100 : 0;
  });
  const abnormalMax = Math.max(...repair.map((r, i) => r + locked[i]), 1);
  const leftAxisMax = Math.ceil(abnormalMax * 1.5);
  const rightAxisMax = Math.ceil(Math.max(...ratio, 0) * 1.3);
  return {
    chartData: {
      categories,
      dataset: { available, repair, locked, total, ratio },
      axes: {
        left: { id: 'left', position: 'left', name: '房间数', unit: '间', min: 0, max: leftAxisMax },
        right: { id: 'right', position: 'right', name: '比例', unit: '%', min: 0, max: rightAxisMax },
      },
      series: [
        { id: 'repair', name: '维修房', dataKey: 'repair', type: 'bar', axisId: 'left', color: '#faad14', stackId: 'room' },
        { id: 'locked', name: '锁房', dataKey: 'locked', type: 'bar', axisId: 'left', color: '#ff4d4f', stackId: 'room' },
        { id: 'ratio', name: '非正常房间比例', dataKey: 'ratio', type: 'line', axisId: 'right', color: '#722ed1' },
      ],
    },
  };
}

function processCustomerSourceData(dataList: any[], granularity: GranularityType): any {
  const recentData = getRecentDataByGranularity(dataList, granularity);
  const categories = recentData.map((item) => {
    const date = new Date(item.date * 1000);
    return isDailyGranularity(granularity) ? `${date.getMonth() + 1}/${date.getDate()}` : `${date.getMonth() + 1}月`;
  });
  const fit = recentData.map((item) => item.fitRoomNight || 0);
  const team = recentData.map((item) => item.teamRoomNight || 0);
  const member = recentData.map((item) => item.memberRoomNight || 0);
  const unit = recentData.map((item) => item.unitRoomNight || 0);
  const ota = recentData.map((item) => item.otaRoomNight || 0);
  const intro = recentData.map((item) => item.introRoomNight || 0);
  const occupancyRate = recentData.map((item) => Math.round((item.occupancyRate || 0) * 100));
  const maxValue = Math.max(
    ...recentData.map(
      (item) =>
        (item.fitRoomNight || 0) +
        (item.teamRoomNight || 0) +
        (item.memberRoomNight || 0) +
        (item.unitRoomNight || 0) +
        (item.otaRoomNight || 0) +
        (item.introRoomNight || 0)
    ),
    1
  );
  return {
    chartData: {
      categories,
      dataset: { fit, team, member, unit, ota, intro, occupancyRate },
      axes: {
        left: { id: 'left', position: 'left', name: '间夜数', unit: '间', min: 0, max: Math.ceil(maxValue * 1.7) },
        right: { id: 'right', position: 'right', name: '出租率', unit: '%', min: 0, max: 130 },
      },
      series: [
        { id: 'ota', name: 'OTA', dataKey: 'ota', type: 'bar', axisId: 'left', color: CUSTOMER_SOURCE_SERIES_COLORS.ota, stackId: 'customer' },
        { id: 'unit', name: '协议客户', dataKey: 'unit', type: 'bar', axisId: 'left', color: CUSTOMER_SOURCE_SERIES_COLORS.unit, stackId: 'customer' },
        { id: 'intro', name: '介绍渠道', dataKey: 'intro', type: 'bar', axisId: 'left', color: CUSTOMER_SOURCE_SERIES_COLORS.intro, stackId: 'customer' },
        { id: 'member', name: '会员', dataKey: 'member', type: 'bar', axisId: 'left', color: CUSTOMER_SOURCE_SERIES_COLORS.member, stackId: 'customer' },
        { id: 'fit', name: '散客', dataKey: 'fit', type: 'bar', axisId: 'left', color: CUSTOMER_SOURCE_SERIES_COLORS.fit, stackId: 'customer' },
        { id: 'team', name: '团队', dataKey: 'team', type: 'bar', axisId: 'left', color: CUSTOMER_SOURCE_SERIES_COLORS.team, stackId: 'customer' },
        { id: 'occupancyRate', name: '出租率', dataKey: 'occupancyRate', type: 'line', axisId: 'right', color: CUSTOMER_SOURCE_SERIES_COLORS.occupancyRate },
      ],
    },
  };
}

function processDiscountAnalysisData(dataList: any[], granularity: GranularityType): any {
  const recentData = getRecentDataByGranularity(dataList, granularity);
  const categories = recentData.map((item) => {
    const date = new Date(item.date * 1000);
    return isDailyGranularity(granularity) ? `${date.getMonth() + 1}/${date.getDate()}` : `${date.getMonth() + 1}月`;
  });
  const discountRate = recentData.map((item) => Math.round((item.discountRate || 0) * 100));
  const occupancyRate = recentData.map((item) => Math.round((item.occupancyRate || 0) * 100));
  const avgListPrice = recentData.map((item) => Math.round(item.avgListPrice || 0));
  const avgPrice = recentData.map((item) => Math.round(item.avgPrice || 0));
  const maxPrice = Math.max(...avgListPrice, ...avgPrice, 1);
  return {
    chartData: {
      categories,
      dataset: { discountRate, occupancyRate, avgListPrice, avgPrice },
      axes: {
        left: { id: 'left', position: 'left', name: '出租率', unit: '%', min: 0, max: 130 },
        right: { id: 'right', position: 'right', name: '价格', unit: '元', min: 0, max: Math.ceil(maxPrice * 1.2) },
        right2: { id: 'right2', position: 'right', name: '折扣率', unit: '%', min: 0, max: 100 },
      },
      series: [
        { id: 'occupancyRate', name: '出租率', dataKey: 'occupancyRate', type: 'bar', axisId: 'left', color: '#91cc75' },
        { id: 'avgListPrice', name: '挂牌价', dataKey: 'avgListPrice', type: 'line', axisId: 'right', color: '#fac858' },
        { id: 'avgPrice', name: '平均房价', dataKey: 'avgPrice', type: 'line', axisId: 'right', color: '#ee6666' },
        { id: 'discountRate', name: '折扣率', dataKey: 'discountRate', type: 'line', axisId: 'right2', color: '#5470c6' },
      ],
    },
  };
}

function processChannelAnalysisData(dataList: any[], granularity: GranularityType): any {
  const recentData = getRecentDataByGranularity(dataList, granularity);
  // 过去 N 天：按渠道累计间夜数/收入（与「渠道信息」一致）。此前用日均再除以 N，7 天与 30 天柱状图极易看起来相同。
  const totalOta = recentData.reduce((s, i) => s + (i.otaRoomNight || 0), 0);
  const totalUnit = recentData.reduce((s, i) => s + (i.unitRoomNight || 0), 0);
  const totalIntro = recentData.reduce((s, i) => s + (i.introRoomNight || 0), 0);
  const totalMember = recentData.reduce((s, i) => s + (i.memberRoomNight || 0), 0);
  const totalFit = recentData.reduce((s, i) => s + (i.fitRoomNight || 0), 0);
  const totalTeam = recentData.reduce((s, i) => s + (i.teamRoomNight || 0), 0);
  const totalNights = totalOta + totalUnit + totalIntro + totalMember + totalFit + totalTeam;
  const categoryLabels = ['OTA', '协议客户', '介绍渠道', '会员', '散客', '团队'];
  const categories = categoryLabels.map((l) => (l.length > 3 ? l.substring(0, 3) + '...' : l));
  const roomNights = [
    Math.round(totalOta),
    Math.round(totalUnit),
    Math.round(totalIntro),
    Math.round(totalMember),
    Math.round(totalFit),
    Math.round(totalTeam),
  ];
  const rates = roomNights.map((nights) => (totalNights > 0 ? Math.round((nights / totalNights) * 100) : 0));
  const revenue = recentData
    .reduce(
      (acc, item) => {
        const adr = item.adr || 0;
        return [
          acc[0] + (item.otaRoomNight || 0) * adr,
          acc[1] + (item.unitRoomNight || 0) * adr,
          acc[2] + (item.introRoomNight || 0) * adr,
          acc[3] + (item.memberRoomNight || 0) * adr,
          acc[4] + (item.fitRoomNight || 0) * adr,
          acc[5] + (item.teamRoomNight || 0) * adr,
        ];
      },
      [0, 0, 0, 0, 0, 0]
    )
    .map((rev) => Math.round(rev));
  const maxNights = Math.max(...roomNights, 1);
  const maxRevenue = Math.max(...revenue, 1);
  return {
    chartData: {
      categories,
      dataset: { roomNights, rates, revenue },
      axes: {
        left: { id: 'left', position: 'left', name: '间夜数', unit: '间', min: 0, max: Math.ceil(maxNights * 1.2) },
        right: { id: 'right', position: 'right', name: '收入', unit: '元', min: 0, max: Math.ceil(maxRevenue * 1.2) },
        bottom: { id: 'bottom', position: 'bottom', axisLabel: { interval: 0 } },
      },
      series: [
        { id: 'roomNights', name: '间夜数', dataKey: 'roomNights', type: 'bar', axisId: 'left', color: '#5470c6' },
        { id: 'revenue', name: '收入', dataKey: 'revenue', type: 'line', axisId: 'right', color: '#91cc75' },
      ],
    },
  };
}

function processChannelInfoData(dataList: any[], granularity: GranularityType): any {
  const recentData = getRecentDataByGranularity(dataList, granularity);
  const totalOta = recentData.reduce((s, i) => s + (i.otaRoomNight || 0), 0);
  const totalUnit = recentData.reduce((s, i) => s + (i.unitRoomNight || 0), 0);
  const totalIntro = recentData.reduce((s, i) => s + (i.introRoomNight || 0), 0);
  const totalMember = recentData.reduce((s, i) => s + (i.memberRoomNight || 0), 0);
  const totalFit = recentData.reduce((s, i) => s + (i.fitRoomNight || 0), 0);
  const totalTeam = recentData.reduce((s, i) => s + (i.teamRoomNight || 0), 0);
  const totalOtaR = recentData.reduce((s, i) => s + ((i.otaRoomNightRevenue ?? (i.otaRoomNight || 0) * (i.adr || 0))), 0);
  const totalUnitR = recentData.reduce((s, i) => s + ((i.unitRoomNightRevenue ?? (i.unitRoomNight || 0) * (i.adr || 0))), 0);
  const totalIntroR = recentData.reduce((s, i) => s + ((i.introRoomNightRevenue ?? (i.introRoomNight || 0) * (i.adr || 0))), 0);
  const totalMemberR = recentData.reduce((s, i) => s + ((i.memberRoomNightRevenue ?? (i.memberRoomNight || 0) * (i.adr || 0))), 0);
  const totalFitR = recentData.reduce((s, i) => s + ((i.fitRoomNightRevenue ?? (i.fitRoomNight || 0) * (i.adr || 0))), 0);
  const totalTeamR = recentData.reduce((s, i) => s + ((i.teamRoomNightRevenue ?? (i.teamRoomNight || 0) * (i.adr || 0))), 0);
  const avgOtaPrice = totalOta > 0 ? Math.round(totalOtaR / totalOta) : 0;
  const avgUnitPrice = totalUnit > 0 ? Math.round(totalUnitR / totalUnit) : 0;
  const avgIntroPrice = totalIntro > 0 ? Math.round(totalIntroR / totalIntro) : 0;
  const avgMemberPrice = totalMember > 0 ? Math.round(totalMemberR / totalMember) : 0;
  const avgFitPrice = totalFit > 0 ? Math.round(totalFitR / totalFit) : 0;
  const avgTeamPrice = totalTeam > 0 ? Math.round(totalTeamR / totalTeam) : 0;
  const categoryLabels = ['OTA', '协议客户', '介绍渠道', '会员', '散客', '团队'];
  const categories = categoryLabels.map((l) => (l.length > 3 ? l.substring(0, 3) + '...' : l));
  const orderCounts = [Math.round(totalOta), Math.round(totalUnit), Math.round(totalIntro), Math.round(totalMember), Math.round(totalFit), Math.round(totalTeam)];
  const avgPrices = [avgOtaPrice, avgUnitPrice, avgIntroPrice, avgMemberPrice, avgFitPrice, avgTeamPrice];
  return {
    chartData: {
      categories,
      dataset: { volume: orderCounts, customerUnitPrice: avgPrices },
      axes: {
        left: { id: 'left', position: 'left', name: '订单量', unit: '单', min: 0, max: Math.ceil(Math.max(...orderCounts, 1) * 1.2) },
        right: { id: 'right', position: 'right', name: '客单价', unit: '元', min: 0, max: Math.ceil(Math.max(...avgPrices, 1) * 1.2) },
        bottom: { id: 'bottom', position: 'bottom', axisLabel: { interval: 0 } },
      },
      series: [
        { id: 'volume', name: '订单量', dataKey: 'volume', type: 'bar', axisId: 'left', color: '#5470c6' },
        { id: 'customerUnitPrice', name: '客单价', dataKey: 'customerUnitPrice', type: 'line', axisId: 'right', color: '#91cc75' },
      ],
    },
  };
}

function processOccupancyAnalysisData(dataList: any[], granularity: GranularityType): any {
  const recentData = getRecentDataByGranularity(dataList, granularity);
  const categories = recentData.map((item) => {
    const date = new Date(item.date * 1000);
    return isDailyGranularity(granularity) ? `${date.getMonth() + 1}/${date.getDate()}` : `${date.getMonth() + 1}月`;
  });
  const occupancyRate = recentData.map((item) => Math.round((item.occupancyRate || 0) * 100));
  const roomOvernightRate = recentData.map((item) => Math.round((item.roomOvernightRate || 0) * 100));
  const hourScale = recentData.map((item) => Math.round((item.hourScale || 0) * 100));
  const roomNight = recentData.map((item) => item.roomNight || 0);
  const maxNight = Math.max(...roomNight, 1);
  return {
    chartData: {
      categories,
      dataset: { occupancyRate, roomOvernightRate, hourScale, roomNight },
      axes: {
        left: { id: 'left', position: 'left', name: '出租率', unit: '%', min: 0, max: 150 },
        right: { id: 'right', position: 'right', name: '间夜数', unit: '间', min: 0, max: Math.ceil(maxNight * 1.3) },
      },
      series: [
        { id: 'occupancyRate', name: '总出租率', dataKey: 'occupancyRate', type: 'line', axisId: 'left', color: '#5470c6' },
        { id: 'roomOvernightRate', name: '过夜房出租率', dataKey: 'roomOvernightRate', type: 'line', axisId: 'left', color: '#91cc75' },
        { id: 'hourScale', name: '钟点房占比', dataKey: 'hourScale', type: 'line', axisId: 'left', color: '#fac858' },
        { id: 'roomNight', name: '间夜数', dataKey: 'roomNight', type: 'bar', axisId: 'right', color: '#ee6666' },
      ],
    },
  };
}

function processOperationEfficiencyData(dataList: any[], granularity: GranularityType): any {
  const recentData = getRecentDataByGranularity(dataList, granularity);
  const categories = recentData.map((item) => {
    const date = new Date(item.date * 1000);
    return isDailyGranularity(granularity) ? `${date.getMonth() + 1}/${date.getDate()}` : `${date.getMonth() + 1}月`;
  });
  const earlyCheckout = recentData.map((item) => item.earlyCheckout || 0);
  const noshow = recentData.map((item) => item.noshow || 0);
  const orderTake = recentData.map((item) => (item.orderRoomTake && item.orderRoomTake > 0 ? item.orderRoomTake : item.roomOvernight || 0));
  const ratio = recentData.map((item, index) => {
    let totalOrders = orderTake[index] + earlyCheckout[index] + noshow[index];
    if (totalOrders === 0 && item.roomOvernight) totalOrders = (item.roomOvernight || 0) + earlyCheckout[index] + noshow[index];
    if (totalOrders === 0 && item.roomNight) totalOrders = Math.round(item.roomNight || 0) + earlyCheckout[index] + noshow[index];
    if (totalOrders === 0) return 0;
    return Math.round(((earlyCheckout[index] + noshow[index]) / totalOrders) * 100 * 100) / 100;
  });
  const maxCount = Math.max(...orderTake, ...earlyCheckout, ...noshow, 1);
  const rightAxisMax = Math.ceil(Math.max(...ratio, 0) * 1.3);
  return {
    chartData: {
      categories,
      dataset: { earlyCheckout, noshow, orderTake, ratio },
      axes: {
        left: { id: 'left', position: 'left', name: '数量', unit: '间', min: 0, max: Math.ceil(maxCount * 1.2) },
        right: { id: 'right', position: 'right', name: '非正常占比', unit: '%', min: 0, max: rightAxisMax },
      },
      series: [
        { id: 'orderTake', name: '预订入住', dataKey: 'orderTake', type: 'bar', axisId: 'left', color: '#52c41a' },
        { id: 'earlyCheckout', name: '提前退房', dataKey: 'earlyCheckout', type: 'bar', axisId: 'left', color: '#faad14' },
        { id: 'noshow', name: '未到店', dataKey: 'noshow', type: 'bar', axisId: 'left', color: '#ff4d4f' },
        { id: 'ratio', name: '非正常占比', dataKey: 'ratio', type: 'line', axisId: 'right', color: '#722ed1' },
      ],
    },
  };
}

export interface OperationAnalysisData {
  roomHealth: any;
  customerSource: any;
  discountAnalysis: any;
  channelAnalysis: any;
  channelInfo: any;
  occupancyAnalysis: any;
  operationEfficiency: any;
}

/**
 * 由日度 businessReport list + 粒度 生成运营分析各组件数据（与后端 /api/revpar/operation-analysis 返回结构一致）
 */
export function buildOperationAnalysisFromList(
  list: any[],
  granularity: GranularityType
): OperationAnalysisData {
  const sorted = [...list].sort((a, b) => (a.date || 0) - (b.date || 0));
  return {
    roomHealth: processRoomHealthData(sorted, granularity),
    customerSource: processCustomerSourceData(sorted, granularity),
    discountAnalysis: processDiscountAnalysisData(sorted, granularity),
    channelAnalysis: processChannelAnalysisData(sorted, granularity),
    channelInfo: processChannelInfoData(sorted, granularity),
    occupancyAnalysis: processOccupancyAnalysisData(sorted, granularity),
    operationEfficiency: processOperationEfficiencyData(sorted, granularity),
  };
}
